import React, { useEffect, useMemo, useState } from 'react';
import { FiCheckCircle, FiClock, FiRefreshCw } from 'react-icons/fi';
import Helmet from '../../component/common/Helmet';
import {
    fetchWorldCup2026Picks,
    saveWorldCup2026Pick,
} from '../../lib/worldCup2026Supabase';
import './worldcup2026.css';

const FIXTURES_URL = 'https://raw.githubusercontent.com/openfootball/worldcup.json/master/2026/worldcup.json';
const ACCESS_PASSWORD = 'worldcup2026!';
const ACCESS_STORAGE_KEY = 'worldcup2026-access-granted';
const USER_STORAGE_KEY = 'worldcup2026-selected-user';

const USERS = [
    { slug: 'matteo', name: 'Matteo' },
    { slug: 'julia', name: 'Julia' },
    { slug: 'kitti', name: 'Kitti' },
];

const createEmptyPicks = () =>
    USERS.reduce((accumulator, user) => {
        accumulator[user.slug] = {};
        return accumulator;
    }, {});

const slugify = (value) =>
    String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

const parseKickoffDate = (date, time) => {
    if (!date || !time) {
        return null;
    }

    const match = String(time).match(/^(\d{1,2}):(\d{2})(?:\s+UTC([+-]\d{1,2}))?$/);
    if (!match) {
        return new Date(`${date}T${time}`);
    }

    const [, hour, minute, offsetValue] = match;
    const [year, month, day] = date.split('-').map(Number);
    const offset = offsetValue ? Number(offsetValue) : 0;

    return new Date(Date.UTC(year, month - 1, day, Number(hour) - offset, Number(minute)));
};

const formatKickoff = (match) => {
    const kickoffDate = parseKickoffDate(match.date, match.time);

    if (!kickoffDate || Number.isNaN(kickoffDate.getTime())) {
        return `${match.date || 'TBD'} ${match.time || ''}`.trim();
    }

    return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
    }).format(kickoffDate);
};

const getMatchId = (match, index) =>
    [
        String(index + 1).padStart(3, '0'),
        slugify(match.round),
        slugify(match.date),
        slugify(match.team1),
        slugify(match.team2),
    ].join('-');

const normalizeFixtures = (payload) =>
    (payload.matches || []).map((match, index) => {
        const kickoffDate = parseKickoffDate(match.date, match.time);

        return {
            ...match,
            id: getMatchId(match, index),
            order: index,
            kickoffDate,
            displayKickoff: formatKickoff(match),
            team1: match.team1 || 'TBD',
            team2: match.team2 || 'TBD',
        };
    });

const getActualScore = (match) => {
    if (!match.score || !Array.isArray(match.score.ft)) {
        return null;
    }

    return {
        home: match.score.ft[0],
        away: match.score.ft[1],
    };
};

const getPredictionStatus = (pick, actualScore) => {
    if (!pick || !actualScore) {
        return null;
    }

    const exactScore = pick.home === actualScore.home && pick.away === actualScore.away;
    const predictedDiff = Math.sign(pick.home - pick.away);
    const actualDiff = Math.sign(actualScore.home - actualScore.away);

    if (exactScore) {
        return 'Exact score';
    }

    if (predictedDiff === actualDiff) {
        return 'Right result';
    }

    return 'Missed';
};

const WorldCup2026 = () => {
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isChoosingUser, setIsChoosingUser] = useState(false);
    const [selectedUser, setSelectedUser] = useState('matteo');
    const [password, setPassword] = useState('');
    const [authError, setAuthError] = useState('');
    const [fixtures, setFixtures] = useState([]);
    const [picks, setPicks] = useState(createEmptyPicks);
    const [draftScores, setDraftScores] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const selectedUserName = USERS.find((user) => user.slug === selectedUser)?.name || 'Player';

    const loadPageData = async (showRefreshMessage = false) => {
        try {
            setErrorMessage('');
            if (showRefreshMessage) {
                setStatusMessage('Refreshing fixtures and predictions...');
            }

            const [fixtureResponse, pickRows] = await Promise.all([
                fetch(FIXTURES_URL),
                fetchWorldCup2026Picks(),
            ]);

            if (!fixtureResponse.ok) {
                throw new Error('Unable to load World Cup fixtures from openfootball.');
            }

            const fixturePayload = await fixtureResponse.json();
            const nextFixtures = normalizeFixtures(fixturePayload);
            const nextPicks = createEmptyPicks();
            const nextDraftScores = {};

            pickRows.forEach((row) => {
                if (!nextPicks[row.user_slug]) {
                    return;
                }

                nextPicks[row.user_slug][row.match_id] = {
                    home: row.predicted_team1_goals,
                    away: row.predicted_team2_goals,
                };

                nextDraftScores[`${row.user_slug}-${row.match_id}`] = {
                    home: String(row.predicted_team1_goals),
                    away: String(row.predicted_team2_goals),
                };
            });

            setFixtures(nextFixtures);
            setPicks(nextPicks);
            setDraftScores(nextDraftScores);
            setStatusMessage(showRefreshMessage ? 'Latest World Cup data loaded.' : '');
        } catch (error) {
            setErrorMessage(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const hasAccess = window.localStorage.getItem(ACCESS_STORAGE_KEY) === 'true';
        const storedUser = window.localStorage.getItem(USER_STORAGE_KEY);

        setIsAuthorized(hasAccess);
        if (hasAccess && storedUser && USERS.some((user) => user.slug === storedUser)) {
            setSelectedUser(storedUser);
            setIsChoosingUser(false);
            loadPageData();
        } else if (hasAccess) {
            setIsChoosingUser(true);
            setIsLoading(false);
        } else {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isAuthorized || isChoosingUser) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            loadPageData();
        }, 60000);

        return () => window.clearInterval(intervalId);
    }, [isAuthorized, isChoosingUser]);

    useEffect(() => {
        if (!statusMessage) {
            return undefined;
        }

        const timeout = window.setTimeout(() => {
            setStatusMessage('');
        }, 2400);

        return () => window.clearTimeout(timeout);
    }, [statusMessage]);

    const completionByUser = useMemo(
        () =>
            USERS.reduce((accumulator, user) => {
                accumulator[user.slug] = Object.values(picks[user.slug] || {}).filter(
                    (pick) => Number.isInteger(pick.home) && Number.isInteger(pick.away)
                ).length;
                return accumulator;
            }, {}),
        [picks]
    );

    const completedMatchCount = fixtures.filter((match) => getActualScore(match)).length;

    const handleAccessSubmit = (event) => {
        event.preventDefault();

        if (password !== ACCESS_PASSWORD) {
            setAuthError('Wrong password.');
            return;
        }

        window.localStorage.setItem(ACCESS_STORAGE_KEY, 'true');
        setIsAuthorized(true);
        setAuthError('');
        setIsChoosingUser(true);
    };

    const handleUserSelection = (userSlug) => {
        window.localStorage.setItem(USER_STORAGE_KEY, userSlug);
        setSelectedUser(userSlug);
        setIsChoosingUser(false);
        setIsLoading(true);
        loadPageData();
    };

    const updateDraftScore = (matchId, side, value) => {
        setDraftScores((currentScores) => ({
            ...currentScores,
            [`${selectedUser}-${matchId}`]: {
                ...(currentScores[`${selectedUser}-${matchId}`] || {}),
                [side]: value,
            },
        }));
    };

    const handleSavePrediction = async (match) => {
        const isStarted = match.kickoffDate ? Date.now() >= match.kickoffDate.getTime() : false;
        if (isStarted) {
            setErrorMessage('This match has already started, so predictions are locked.');
            return;
        }

        const draftScore = draftScores[`${selectedUser}-${match.id}`] || {};
        const home = Number(draftScore.home);
        const away = Number(draftScore.away);

        if (
            draftScore.home === '' ||
            draftScore.away === '' ||
            draftScore.home === undefined ||
            draftScore.away === undefined ||
            !Number.isInteger(home) ||
            !Number.isInteger(away) ||
            home < 0 ||
            away < 0
        ) {
            setErrorMessage('Enter a valid non-negative score before saving.');
            return;
        }

        setIsSaving(true);
        setErrorMessage('');
        setStatusMessage('Saving prediction...');

        const previousPick = picks[selectedUser]?.[match.id];

        setPicks((currentPicks) => ({
            ...currentPicks,
            [selectedUser]: {
                ...currentPicks[selectedUser],
                [match.id]: { home, away },
            },
        }));

        try {
            await saveWorldCup2026Pick({
                user_slug: selectedUser,
                user_name: selectedUserName,
                match_id: match.id,
                match_order: match.order,
                round_name: match.round || '',
                group_name: match.group || '',
                match_date: match.date || null,
                match_time: match.time || '',
                venue: match.ground || '',
                team1: match.team1,
                team2: match.team2,
                predicted_team1_goals: home,
                predicted_team2_goals: away,
                updated_at: new Date().toISOString(),
            });

            setStatusMessage('Prediction saved.');
            await loadPageData();
        } catch (error) {
            setPicks((currentPicks) => ({
                ...currentPicks,
                [selectedUser]: {
                    ...currentPicks[selectedUser],
                    [match.id]: previousPick,
                },
            }));
            setErrorMessage(error.message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="active-dark worldcup2026-page">
            <Helmet pageTitle="World Cup 2026 Predictions" disableTitleSuffix />
            <main className="worldcup2026-shell">
                {!isAuthorized ? (
                    <section className="worldcup2026-auth">
                        <div className="worldcup2026-auth__card">
                            <p className="worldcup2026-kicker">Private Page</p>
                            <h1>World Cup 2026</h1>
                            <p className="worldcup2026-summary">
                                Enter the password to start guessing results.
                            </p>

                            <form className="worldcup2026-auth__form" onSubmit={handleAccessSubmit}>
                                <label htmlFor="worldcup-password">Password</label>
                                <input
                                    id="worldcup-password"
                                    type="password"
                                    value={password}
                                    onChange={(event) => {
                                        setPassword(event.target.value);
                                        if (authError) {
                                            setAuthError('');
                                        }
                                    }}
                                    autoFocus
                                />
                                <button type="submit">Enter</button>
                            </form>

                            {authError ? <div className="worldcup2026-auth__error">{authError}</div> : null}
                        </div>
                    </section>
                ) : isChoosingUser ? (
                    <section className="worldcup2026-auth">
                        <div className="worldcup2026-auth__card">
                            <p className="worldcup2026-kicker">World Cup 2026</p>
                            <h1>Who are you?</h1>
                            <p className="worldcup2026-summary">
                                Choose your player before entering score predictions.
                            </p>

                            <div className="worldcup2026-user-picker">
                                {USERS.map((user) => (
                                    <button key={user.slug} type="button" onClick={() => handleUserSelection(user.slug)}>
                                        {user.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </section>
                ) : (
                    <>
                        <section className="worldcup2026-hero">
                            <div>
                                <p className="worldcup2026-kicker">June 11 - July 19, 2026</p>
                                <h1>World Cup 2026</h1>
                                <p className="worldcup2026-summary">
                                    Guess the exact score for every match. Fixtures and actual results refresh from openfootball.
                                </p>
                            </div>
                            <div className="worldcup2026-scoreboard">
                                <div>
                                    <span>{selectedUserName}</span>
                                    <strong>{completionByUser[selectedUser] || 0}/{fixtures.length || 104}</strong>
                                </div>
                                <div>
                                    <span>Results in</span>
                                    <strong>{completedMatchCount}/{fixtures.length || 104}</strong>
                                </div>
                            </div>
                        </section>

                        <section className="worldcup2026-toolbar">
                            <div className="worldcup2026-user-meta">
                                <strong>{selectedUserName}</strong>
                                <button type="button" onClick={() => setIsChoosingUser(true)}>
                                    Switch player
                                </button>
                            </div>
                            <div className="worldcup2026-toolbar__actions">
                                <button type="button" onClick={() => loadPageData(true)} disabled={isLoading || isSaving}>
                                    <FiRefreshCw />
                                    Refresh
                                </button>
                                <span>{isLoading ? 'Loading World Cup data...' : statusMessage}</span>
                            </div>
                        </section>

                        {errorMessage ? <div className="worldcup2026-banner worldcup2026-banner--error">{errorMessage}</div> : null}

                        <section className="worldcup2026-banner">
                            <FiClock />
                            <span>
                                Predictions are stored privately in Supabase. Official scores come from the public openfootball JSON feed after matches are updated there.
                            </span>
                        </section>

                        <section className="worldcup2026-matches">
                            {fixtures.map((match) => {
                                const actualScore = getActualScore(match);
                                const pick = picks[selectedUser]?.[match.id];
                                const draftScore = draftScores[`${selectedUser}-${match.id}`] || {
                                    home: pick ? String(pick.home) : '',
                                    away: pick ? String(pick.away) : '',
                                };
                                const predictionStatus = getPredictionStatus(pick, actualScore);
                                const isStarted = match.kickoffDate ? Date.now() >= match.kickoffDate.getTime() : false;

                                return (
                                    <article key={match.id} className="worldcup2026-match">
                                        <header>
                                            <div>
                                                <p>{match.group || match.round || 'Match'}</p>
                                                <h2>{match.team1} vs {match.team2}</h2>
                                            </div>
                                            <span>{match.displayKickoff}</span>
                                        </header>

                                        <div className="worldcup2026-match__meta">
                                            <span>{match.round}</span>
                                            <span>{match.ground}</span>
                                            {isStarted ? <span>Started</span> : <span>Open</span>}
                                        </div>

                                        <div className="worldcup2026-prediction-row">
                                            <label>
                                                <span>{match.team1}</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    inputMode="numeric"
                                                    value={draftScore.home}
                                                    onChange={(event) => updateDraftScore(match.id, 'home', event.target.value)}
                                                    disabled={isStarted}
                                                />
                                            </label>
                                            <strong>-</strong>
                                            <label>
                                                <span>{match.team2}</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    inputMode="numeric"
                                                    value={draftScore.away}
                                                    onChange={(event) => updateDraftScore(match.id, 'away', event.target.value)}
                                                    disabled={isStarted}
                                                />
                                            </label>
                                            <button type="button" onClick={() => handleSavePrediction(match)} disabled={isSaving || isStarted}>
                                                Save
                                            </button>
                                        </div>

                                        <footer>
                                            <span>
                                                Your pick: {pick ? `${pick.home}-${pick.away}` : 'Not guessed yet'}
                                            </span>
                                            <span>
                                                Actual: {actualScore ? `${actualScore.home}-${actualScore.away}` : 'Pending'}
                                            </span>
                                            {predictionStatus ? (
                                                <strong className={`worldcup2026-status is-${slugify(predictionStatus)}`}>
                                                    <FiCheckCircle />
                                                    {predictionStatus}
                                                </strong>
                                            ) : null}
                                        </footer>
                                    </article>
                                );
                            })}
                        </section>
                    </>
                )}
            </main>
        </div>
    );
};

export default WorldCup2026;
